import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck } from "lucide-react";

const HERO_PANELS = [
  { src: "https://images.unsplash.com/photo-1571838045421-4845dc4f5979?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Sun-dried red chillies", cls: "w-2/5 sm:w-1/2" },
  { src: "https://images.unsplash.com/photo-1741513599050-487ccfb86275?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Finger turmeric", cls: "w-[30%] sm:w-1/4" },
  { src: "https://customer-assets-cm19k8pv.emergentagent.net/job_tsuigva-global/artifacts/a5ea5abc7befec64_1E60C092-4DAB-490F-ACE8-3ADD5C3A8E3F.png", alt: "Tsugiva cold pressed groundnut and coconut oil bottles", cls: "w-[30%] sm:w-1/4" },
];

const stats = [
  { value: "7+", label: "Export Product Lines" },
  { value: "12+", label: "Countries Served" },
  { value: "<45°C", label: "Cold Press Extraction" },
  { value: "100%", label: "Lab-Tested Batches" },
];

const scrollTo = (e, href) => {
  e.preventDefault();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-night">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <div className="flex h-full" data-testid="hero-image-collage">
          {HERO_PANELS.map((p, i) => (
            <motion.div
              key={p.src}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
              className={`h-full overflow-hidden ${p.cls}`}
            >
              <img src={p.src} alt={p.alt} className="h-full w-full object-cover" />
            </motion.div>
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-night/95 via-night/80 to-night/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-night via-transparent to-night/50" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-4 pb-24 pt-32 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-px w-12 bg-amberwarm" />
          <span data-testid="hero-eyebrow" className="font-mono text-xs uppercase tracking-[0.28em] text-amberwarm sm:text-sm">
            Pure Agro Commodities & Cold Pressed Oils
          </span>
        </motion.div>

        <h1 className="max-w-4xl font-serif text-4xl font-bold leading-[1.05] tracking-tight text-cream sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.3}>From India's Soil,</MaskedLine>
          <MaskedLine delay={0.48} className="italic text-amberwarm">
            To the World's Table.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
        >
          Premium finger turmeric, sun-dried red chillies in every grade and 100% cold pressed oils — shipped
          to importers worldwide, and bottled in home packs for kitchens across India.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#shop"
            data-testid="hero-shop-home-button"
            onClick={(e) => scrollTo(e, "#shop")}
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-night transition-transform duration-300 hover:scale-[1.04]"
          >
            Shop for Your Home
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#quote"
            data-testid="hero-get-quote-button"
            onClick={(e) => scrollTo(e, "#quote")}
            className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-7 py-3.5 text-sm font-semibold text-cream backdrop-blur-sm transition-colors duration-300 hover:border-amberwarm hover:text-amberwarm"
          >
            Get Wholesale Quote
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-16 grid max-w-2xl grid-cols-2 gap-6 border-t border-cream/15 pt-8 sm:grid-cols-4"
        >
          {stats.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="font-serif text-2xl font-bold text-amberwarm sm:text-3xl">{s.value}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-cream/60">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="absolute bottom-28 right-10 z-10 hidden lg:block"
      >
        <div className="animate-float-slow flex items-center gap-3 rounded-2xl border border-cream/15 bg-night/60 px-5 py-4 backdrop-blur-md">
          <BadgeCheck className="text-amberwarm" size={26} />
          <div>
            <div className="text-sm font-semibold text-cream">100% Cold Pressed</div>
            <div className="text-xs text-cream/60">Below 45°C — zero solvents</div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
